import React from "react";
import { Modal as AntModal } from "antd";
import type { ModalProps as AntModalProps } from "antd";

import Button from "../Button/Button";
import "./stylesModal.css";

export type ModalSize = "sm" | "md" | "lg" | "xl";
export type ModalIntent = "default" | "destructive";

const sizeToWidth: Record<ModalSize, number> = {
  sm: 400,
  md: 500,
  lg: 600,
  xl: 800,
};

export type ModalAction = {
  label: React.ReactNode;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
};

export type ModalProps = Omit<
  AntModalProps,
  "open" | "onCancel" | "onOk" | "footer" | "title" | "width"
> & {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  size?: ModalSize;
  intent?: ModalIntent;
  primaryAction?: ModalAction;
  secondaryAction?: ModalAction;
  /** Pass `null` to hide the footer; pass a node to fully replace it. */
  footer?: React.ReactNode | null;
  width?: number | string;
  children?: React.ReactNode;
};

function Modal({
  open,
  onClose,
  title,
  size = "md",
  intent = "default",
  primaryAction,
  secondaryAction,
  footer,
  width,
  className = "",
  children,
  ...rest
}: ModalProps) {
  const resolvedWidth = width ?? sizeToWidth[size];

  let resolvedFooter: React.ReactNode | null;
  if (footer === undefined) {
    if (!primaryAction && !secondaryAction) {
      resolvedFooter = null;
    } else {
      resolvedFooter = (
        <div className="pam-modal__footer">
          {secondaryAction && (
            <Button
              intent="neutral"
              variant="outline"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
            >
              {secondaryAction.label}
            </Button>
          )}
          {primaryAction && (
            <Button
              intent={intent === "destructive" ? "danger" : "primary"}
              loading={primaryAction.loading}
              disabled={primaryAction.disabled}
              onClick={primaryAction.onClick}
            >
              {primaryAction.label}
            </Button>
          )}
        </div>
      );
    }
  } else {
    resolvedFooter = footer;
  }

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      title={title}
      width={resolvedWidth}
      footer={resolvedFooter}
      className={`pam-modal pam-modal--${size} ${className}`.trim()}
      {...rest}
    >
      {children}
    </AntModal>
  );
}

export default Modal;
